import { apiPost, apiPublic } from '../../../adapters/api-client/api';
import {
  saveTokens,
  clearTokens,
  getStoredTokens,
} from '../../../adapters/api-client/tokenStorage';
import {
  PASSWORD_BLOCKLISTED_CODE,
  PASSWORD_BLOCKLISTED_MESSAGE_ES,
  PASSWORD_PATTERN,
  PASSWORD_POLICY_MESSAGE_ES,
} from '@iqmaxx/shared';

/**
 * Every call here goes through the backend auth controller rather than talking
 * to Cognito directly.
 *
 * This matters beyond consistency: `POST /auth/register` creates the DynamoDB
 * user record (and captures terms/privacy acceptance, rejects duplicate emails
 * and notifies admins of teacher signups), and `POST /auth/confirm-registration`
 * flips that record to VERIFIED. Signing up straight against Cognito produces an
 * identity with no platform profile behind it, which then fails every
 * role-gated route.
 */

/** Backend registration roles. The Cognito `custom:role` claim carries these verbatim. */
/** DEC-S2-S01-01: no learner account exists; a learner lives in the Parent's household. */
export type RegisterRole = 'PARENT' | 'TEACHER';

/**
 * The shared password policy (PG-02, TCH-02-FR-03), so the form, the backend
 * and Cognito cannot disagree. This used to be its own 8-character regex
 * demanding a symbol, and refusing any character outside A–Z, digits and
 * @$!%*?& — while the governed rule is 15 characters with no composition rules.
 */
export const PASSWORD_RULE = PASSWORD_PATTERN;
export const PASSWORD_RULE_MESSAGE = PASSWORD_POLICY_MESSAGE_ES;

/**
 * TCH-02-FR-03 / GAP-T01-15: the server refuses passwords on the blocklist
 * (Have I Been Pwned + a bundled list). The form cannot check that itself, so
 * it recognises the refusal by its code and shows the shared message on the
 * password field. One message whichever list matched, on purpose.
 */
export const PASSWORD_BLOCKLISTED_MESSAGE = PASSWORD_BLOCKLISTED_MESSAGE_ES;
export const isPasswordBlocklisted = (error: unknown): boolean =>
  (error as { body?: { code?: unknown } } | null)?.body?.code === PASSWORD_BLOCKLISTED_CODE;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: RegisterRole;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
}

export interface RegisterResult {
  userId: string;
  email: string;
  verificationRequired: boolean;
  message: string;
}

interface SignedInResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  user: { userId: string; email: string; role: string; firstName: string; lastName: string };
}

/**
 * Sign-in stopped at the second factor (Operator MFA, CTO decision
 * 12 Oct 2026; ADM-18-FR-05). Every ADMIN/MODERATOR gets one: MFA_SETUP the
 * first time (add the account to an authenticator, then type its code),
 * SOFTWARE_TOKEN_MFA after that. `session` is opaque — send it back as is.
 */
export interface MfaChallenge {
  challenge: 'MFA_SETUP' | 'SOFTWARE_TOKEN_MFA';
  session: string;
  secretCode?: string;
  otpauthUri?: string;
}

export interface SignedIn {
  user: { id: string; email: string; role: string; firstName: string; lastName: string };
  accessToken: string;
}

export const isMfaChallenge = (result: SignedIn | MfaChallenge): result is MfaChallenge =>
  typeof (result as MfaChallenge).challenge === 'string';

/** Tokens are stored only once sign-in is complete, never on a challenge. */
const completeSignIn = (response: SignedInResponse): SignedIn => {
  saveTokens({
    idToken: response.idToken,
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    expiresIn: response.expiresIn,
  });

  return {
    user: {
      id: response.user.userId,
      email: response.user.email,
      role: response.user.role,
      firstName: response.user.firstName,
      lastName: response.user.lastName,
    },
    accessToken: response.accessToken,
  };
};

export const authService = {
  /**
   * Login via backend API (credentials proxied through the API, not sent
   * directly to Cognito). Resolves to the signed-in user, or to the second
   * factor the backend asks for.
   */
  login: async (credentials: LoginCredentials): Promise<SignedIn | MfaChallenge> => {
    const response = await apiPublic<SignedInResponse | MfaChallenge>(
      'POST',
      '/api/v1/auth/login',
      credentials,
    );
    if (isMfaChallenge(response as SignedIn | MfaChallenge)) return response as MfaChallenge;
    return completeSignIn(response as SignedInResponse);
  },

  /** Finish a sign-in that stopped at a challenge, with the authenticator's 6-digit code. */
  verifyMfa: async (session: string, code: string): Promise<SignedIn> =>
    completeSignIn(
      await apiPublic<SignedInResponse>('POST', '/api/v1/auth/mfa/verify', { session, code }),
    ),

  /** Register a new account. Creates the Cognito identity AND the DynamoDB user record. */
  register: (userData: RegisterData): Promise<RegisterResult> =>
    apiPublic<RegisterResult>('POST', '/api/v1/auth/register', userData),

  /** Confirm the emailed verification code and mark the user VERIFIED. */
  verifyEmail: (data: { email: string; code: string }) =>
    apiPublic<{ message: string; email: string; confirmed: boolean }>(
      'POST',
      '/api/v1/auth/confirm-registration',
      { email: data.email, confirmationCode: data.code },
    ),

  /** Re-send the verification code to an unconfirmed account. */
  resendVerification: (data: { email: string }) =>
    apiPublic<{ message: string; email: string }>('POST', '/api/v1/auth/resend-confirmation', {
      email: data.email,
    }),

  /** Request a password reset code. */
  forgotPassword: (data: { email: string }) =>
    apiPublic<{ message: string }>('POST', '/api/v1/auth/forgot-password', { email: data.email }),

  /** Complete a password reset with the emailed code. */
  resetPassword: (data: { email: string; code: string; newPassword: string }) =>
    apiPublic<{ message: string }>('POST', '/api/v1/auth/reset-password', data),

  /**
   * Ends the session, server-side and locally.
   *
   * It used to only clear localStorage. `POST /auth/logout` has always
   * existed and calls Cognito GlobalSignOut to revoke the session — nothing
   * called it, so signing out forgot the token in this browser and left the
   * session valid everywhere else. On a shared computer that is not a sign-out.
   *
   * The local clear happens whether or not the server call succeeds: a user
   * who cannot reach us must still be able to sign out of this browser. When
   * revocation fails the caller is told, because "signed out here only" and
   * "signed out" are different facts.
   *
   * The access token goes in the body because GlobalSignOut refuses the ID token
   * every request is sent with. Read after any refresh the call itself makes, or
   * an expired session would send the token it had just replaced.
   */
  logout: async (): Promise<{ revoked: boolean }> => {
    try {
      await apiPost<{ message: string }>('/api/v1/auth/logout', () => ({
        accessToken: getStoredTokens().accessToken,
      }));
      return { revoked: true };
    } catch {
      // Deliberate: reported to the caller rather than thrown, so the local
      // sign-out below always happens.
      return { revoked: false };
    } finally {
      clearTokens();
    }
  },

  /**
   * DEC-S2-S01-06 — Student Sign Out: this device's credentials and no others.
   *
   * `logout` above reaches every device of the account, and the server refuses
   * it in kid mode: the account is the Parent's, and their phone is not the
   * child's to sign out. This revokes the refresh token held here. Local tokens
   * go whatever the server says, as above, and a failure is reported the same way.
   */
  signOutStudent: async (): Promise<{ revoked: boolean }> => {
    try {
      const { refreshToken } = getStoredTokens();
      if (!refreshToken) return { revoked: false };
      await apiPost<{ message: string }>('/api/v1/context/kid-mode/sign-out', { refreshToken });
      return { revoked: true };
    } catch {
      return { revoked: false };
    } finally {
      clearTokens();
    }
  },
};
